import { useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import {
  ArrowLeft,
  ChevronRight,
  Loader2,
  LogOut,
  Send,
  } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ROUTES } from '@/config/routes';
import { onboardingService } from '@/services/onboardingService';
import { AppLogo } from '@/components/layout/AppLogo';
import { useAuthStore } from '@/store/authStore';
import { cn } from '@/lib/utils';
import {
  DRAFT_STORAGE_KEY,
  ONBOARDING_PENDING_KEY,
  STEPS,
} from '@/pages/organization-registration/constants';
import {
  buildDraftPayload,
  buildInitialFormState,
  buildRegistrationPayload,
  EMPTY_FORM,
  getContactName,
  validateStep,
} from '@/pages/organization-registration/helpers';
import type { FieldError, FormState, HttpErrorShape } from '@/pages/organization-registration/types';
import { Stepper } from '@/pages/organization-registration/components';
import { Step1, Step2Combined, Step3Review } from '@/pages/organization-registration/steps';

// ---------- main page ----------

export default function OrganizationRegistrationPage() {
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();

  // Guard: check access status on mount and redirect if the user already has
  // an organization or is in a state that doesn't belong on this page.
  const [accessChecked, setAccessChecked] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const fallbackRedirect = () => {
      if (user?.organizationId) {
        navigate(ROUTES.HOME, { replace: true });
        return;
      }
      if (window.localStorage.getItem(ONBOARDING_PENDING_KEY) === '1') {
        navigate(ROUTES.WAITING_APPROVAL, { replace: true });
        return;
      }
      setAccessChecked(true);
    };

    onboardingService
      .getAccessStatus()
      .then((status) => {
        if (cancelled) return;
        if (status.state === 'approved') {
          navigate(ROUTES.HOME, { replace: true });
        } else if (status.state === 'pending_approval') {
          navigate(ROUTES.WAITING_APPROVAL, { replace: true });
        } else if (status.state === 'rejected') {
          navigate(ROUTES.REJECTED_ORGANIZATION, { replace: true });
        } else if (status.state === 'suspended') {
          navigate(ROUTES.WAITING_APPROVAL, { replace: true });
        } else {
          // 'no_organization' — user is allowed here
          setAccessChecked(true);
        }
      })
      .catch(() => {
        if (cancelled) return;
        fallbackRedirect();
      });
    return () => {
      cancelled = true;
    };
  }, [navigate, user?.organizationId]);

  const [form, setForm] = useState<FormState>(() => buildInitialFormState(user));

  // If the user info from Keycloak hydrates after this page mounts, backfill the
  // contact name once — but never overwrite a value the user already typed.
  useEffect(() => {
    const fullName = getContactName(user);
    if (!fullName) return;
    setForm((prev) => (prev.contactPerson.trim() ? prev : { ...prev, contactPerson: fullName }));
  }, [user]);
  const [step, setStep] = useState(1);
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const scrollToTop = () => {
    scrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Autosave the draft on form changes.
  useEffect(() => {
    const t = window.setTimeout(() => {
      try {
        const draft = buildDraftPayload(form);
        window.localStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(draft));
      } catch {
        // Ignore errors
      }
    }, 500);
    return () => window.clearTimeout(t);
  }, [form]);

  const stepErrors = useMemo(() => validateStep(step, form), [step, form]);
  const allErrors = useMemo(
    () => ({
      ...validateStep(1, form),
      ...validateStep(2, form),
      ...validateStep(3, form),
    }),
    [form],
  );

  const update = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };
  const markTouched = (key: keyof FormState) => setTouched((prev) => ({ ...prev, [key]: true }));

  const showError = (key: keyof FormState): FieldError =>
    touched[key] || step === 3 ? stepErrors[key] : undefined;

  const fieldIsValid = (key: keyof FormState): boolean => {
    const value = form[key];
    if (typeof value === 'string' && value.trim().length === 0) return false;
    return !stepErrors[key];
  };

  const goNext = () => {
    const errors = validateStep(step, form);
    const nextTouched: Record<string, boolean> = { ...touched };
    Object.keys(errors).forEach((k) => {
      nextTouched[k] = true;
    });
    setTouched(nextTouched);
    if (Object.keys(errors).length > 0) {
      toast.error('Please complete the highlighted fields before continuing.');
      return;
    }
    setStep((s) => Math.min(STEPS.length, s + 1));
    scrollToTop();
  };

  const goBack = () => {
    setStep((s) => Math.max(1, s - 1));
    scrollToTop();
  };

  const editStep = (target: number) => {
    setStep(target);
    scrollToTop();
  };

  const onSubmit = async () => {
    if (Object.keys(allErrors).length > 0 || isSubmitting) {
      toast.error('Please review the highlighted fields before submitting.');
      const everyKey = Object.keys(allErrors).reduce<Record<string, boolean>>((acc, k) => {
        acc[k] = true;
        return acc;
      }, {});
      setTouched((prev) => ({ ...prev, ...everyKey }));
      return;
    }

    setIsSubmitting(true);
    let submitted = false;
    try {
      // TODO: Handle logo file upload
      // The logo file (form.logo) would need to be uploaded separately
      // or converted to base64 and included in the registration payload
      // This depends on the backend API implementation

      const payload = buildRegistrationPayload(form);
      await onboardingService.registerOrganization(payload);
      submitted = true;

      window.localStorage.setItem(ONBOARDING_PENDING_KEY, '1');
      window.localStorage.removeItem(DRAFT_STORAGE_KEY);
      toast.success('Organization submitted for review.');

      // Hard-reload into the waiting page rather than a client-side navigate.
      // A full load re-runs Keycloak init and every onboarding status check
      // against the server, so the app cannot keep serving a stale
      // 'no_organization' state that puts a live registration form back in
      // front of someone who already registered. It also drops the form's
      // in-memory state and its history entry, so Back cannot return to it.
      window.location.replace(`${window.location.origin}${ROUTES.WAITING_APPROVAL}`);
      return;
    } catch (error: unknown) {
      const err = error as HttpErrorShape;
      const status = err.response?.status;
      if (status === 409) {
        window.localStorage.setItem(ONBOARDING_PENDING_KEY, '1');
        window.localStorage.removeItem(DRAFT_STORAGE_KEY);
        toast.info(err.response?.data?.message ?? 'A registration request already exists for your account.');
        navigate(ROUTES.WAITING_APPROVAL, { replace: true });
      } else {
        toast.error(err.response?.data?.message ?? 'We could not submit your request. Please try again.');
      }
    } finally {
      // On success the page is mid-reload; re-enabling the submit button would
      // leave a live control on screen during that gap. Only the error paths,
      // where the user stays on the form, need it back.
      if (!submitted) {
        setIsSubmitting(false);
      }
    }
  };

  const clearDraft = () => {
    if (confirm('Are you sure you want to clear your draft? All progress will be lost.')) {
      window.localStorage.removeItem(DRAFT_STORAGE_KEY);
      setForm(EMPTY_FORM);
      setTouched({});
      setStep(1);
      toast.success('Draft cleared.');
    }
  };

  const isLastStep = step === STEPS.length;
  const currentStep = STEPS.find((s) => s.id === step)!;

  // Show a spinner while the access status check is in flight.
  if (!accessChecked) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  return (
    <div
      className="h-screen flex flex-col text-foreground overflow-hidden relative z-0"
      style={{ background: 'linear-gradient(135deg, var(--kibo-page-from) 0%, var(--kibo-page-to) 100%) !important' }}
    >
      {/* Top Bar */}
      <header className="fixed top-0 w-full z-50 flex justify-between items-center px-8 h-20 bg-card/80 backdrop-blur-xl border-b border-border/60">
        <div className="flex items-center gap-4">
          <AppLogo variant="full" alt="BlueB" className="h-10 w-auto" />
          <div className="h-6 w-px bg-border/50 mx-2 hidden sm:block"></div>
          <h1 className="font-heading text-xl sm:text-2xl font-bold tracking-tight text-foreground">Organization Registration</h1>
        </div>
        <div className="flex items-center gap-4">
          <button
            onClick={logout}
            className="text-sm font-medium text-muted-foreground hover:text-primary transition-colors flex items-center gap-2"
          >
            <LogOut size={18} />
            Sign out
          </button>
        </div>
      </header>

      <div className="flex flex-1 pt-20 overflow-hidden">
        {/* Main Content */}
        <main ref={scrollRef} className="flex-1 p-6 md:p-10 overflow-y-auto">
          <div className="max-w-3xl mx-auto pb-8">
            <div className="bg-card shadow-md rounded-2xl border border-border p-6 sm:p-8">
              {/* Stepper inside card */}
              <Stepper current={step} className="mb-6 pb-6 border-b border-border/60" />

              <AnimatePresence mode="wait">
                <motion.div
                  key={step}
                  initial={{ opacity: 0, x: 16 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -16 }}
                  transition={{ duration: 0.25, ease: 'easeInOut' }}
                >
                  <div className="mb-6">
                    <h2 className="font-heading text-xl font-semibold tracking-tight text-foreground">
                      {currentStep.title}
                    </h2>
                  </div>

                  {step === 1 && (
                    <Step1
                      form={form}
                      update={update}
                      markTouched={markTouched}
                      showError={showError}
                      fieldIsValid={fieldIsValid}
                      isSubmitting={isSubmitting}
                    />
                  )}
                  {step === 2 && (
                    <Step2Combined
                      form={form}
                      update={update}
                      markTouched={markTouched}
                      showError={showError}
                      fieldIsValid={fieldIsValid}
                      userEmail={user?.email}
                      isSubmitting={isSubmitting}
                    />
                  )}
                  {step === 3 && (
                    <Step3Review
                      form={form}
                      update={update}
                      showError={showError}
                      isSubmitting={isSubmitting}
                      onEdit={editStep}
                    />
                  )}

                  {/* Navigation Buttons */}
                  <div className="flex flex-col-reverse sm:flex-row items-center justify-between gap-4 pt-6 border-t border-border mt-8">
                    <button
                      type="button"
                      className="text-sm font-semibold text-muted-foreground hover:text-foreground transition-colors px-4 py-2 cursor-pointer"
                      onClick={clearDraft}
                    >
                      Clear draft
                    </button>
                    <div className="flex items-center gap-4 w-full sm:w-auto">
                      {step > 1 && (
                        <button
                          type="button"
                          onClick={goBack}
                          disabled={isSubmitting}
                          className="flex-1 sm:flex-none border-2 border-primary text-primary font-semibold px-6 py-2.5 rounded-xl transition-all hover:bg-primary/5 flex items-center justify-center gap-2 cursor-pointer"
                        >
                          <ArrowLeft size={18} />
                          Previous
                        </button>
                      )}
                      {!isLastStep ? (
                        <button
                          type="button"
                          onClick={goNext}
                          disabled={isSubmitting}
                          className="flex-1 sm:flex-none bg-primary hover:bg-primary/90 text-white font-semibold px-8 py-2.5 rounded-xl transition-all glow-primary flex items-center justify-center gap-2 cursor-pointer"
                        >
                          Next
                          <ChevronRight size={18} />
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={onSubmit}
                          disabled={isSubmitting || !form.acknowledgeAccuracy}
                          className={cn(
                            'flex-1 sm:flex-none text-white font-semibold px-10 py-2.5 rounded-xl transition-all flex items-center justify-center gap-2 shadow-soft cursor-pointer',
                            form.acknowledgeAccuracy && !isSubmitting
                              ? 'bg-primary hover:bg-primary/90 glow-primary transform hover:-translate-y-[1px]'
                              : 'bg-muted-foreground/30 cursor-not-allowed opacity-50'
                          )}
                        >
                          {isSubmitting ? (
                            <>
                              <Loader2 className="h-4 w-4 animate-spin" />
                              Submitting…
                            </>
                          ) : (
                            <>
                              Submit Registration
                              <Send size={18} />
                            </>
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
